/**
 * Spinner / LoadingOverlay — DS-45, DS-48.
 *
 * The only busy indicator in the system. Motion exists here because the user is
 * genuinely waiting; nothing else in the design system animates.
 */

import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';

const SPINNER_SIZES: Record<SpinnerSize, string> = {
  xs: 'h-3 w-3',
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4', // DS-45 — matches an icon inside a button
  lg: 'h-6 w-6',
};

interface SpinnerProps extends Omit<LucideProps, 'size' | 'ref'> {
  size?: SpinnerSize;
  /** Accessible name; the glyph itself carries no meaning. */
  label?: string;
}

export const Spinner = forwardRef<SVGSVGElement, SpinnerProps>(function Spinner(
  { size = 'md', label, className, ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  const name = label ?? t('status.loading');

  return (
    <Loader2
      ref={ref}
      role="status"
      aria-label={name}
      className={cn('shrink-0 animate-spin', SPINNER_SIZES[size], className)}
      {...rest}
    />
  );
});

interface LoadingOverlayProps extends HTMLAttributes<HTMLDivElement> {
  /** Render nothing when false, so a caller can bind it straight to `isFetching`. */
  show?: boolean;
  /** Caption under the spinner; pass an empty string to hide it. */
  label?: string;
  /** Cover the viewport rather than the nearest positioned ancestor. */
  fixed?: boolean;
}

/**
 * Covers the region it sits in — the parent needs `relative`. Deliberately
 * translucent: on a dense screen the user should still see the shape of the
 * table underneath, so the layout does not appear to disappear and come back.
 */
export const LoadingOverlay = forwardRef<HTMLDivElement, LoadingOverlayProps>(function LoadingOverlay(
  { show = true, label, fixed = false, className, ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  // `??` not `||`: an explicit '' means "no caption", which is not the same as
  // "no label given" and must not fall back to the default.
  const caption = label ?? t('status.loading');

  if (!show) return null;

  return (
    <div
      ref={ref}
      aria-live="polite"
      aria-busy
      className={cn(
        'z-20 flex flex-col items-center justify-center gap-2 bg-white/70',
        fixed ? 'fixed inset-0' : 'absolute inset-0',
        className,
      )}
      {...rest}
    >
      <Spinner size="lg" className="text-blue-600" label={caption || t('status.loading')} />
      {caption ? <span className="label-micro">{caption}</span> : null}
    </div>
  );
});
